/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { useMsal } from '@azure/msal-react';

import { useAuth } from '../../hooks/useAuth';
import useAxios from '../../hooks/useAxios';
import { handleError } from '../../utils';
import './Login.scss';
import MailIcon from '../../assets/icons/mail-icon.svg';
import LockIcon from '../../assets/icons/lock-icon.svg';
import SSOIcon from '../../assets/icons/sso-icon.svg';

const Login = () => {
  const toast = useRef(null);
  const toastBottom = useRef(null);

  const { instance } = useMsal();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const { login } = useAuth();
  const { post, apiLoading, apiError } = useAxios();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired') !== null && sessionStorage.getItem('sessionExpired') === 'true') {
      sessionStorage.removeItem('sessionExpired');
      toastBottom.current?.show({
        severity: 'warn',
        summary: 'Session Expired',
        detail: 'Your session has expired. Please login again.',
        life: 5000
      });
    }
  }, []);

  const initializeSignIn = () => {
    const params = new URLSearchParams(location.search);
    const redirectPath = params.get('redirect');
    if (redirectPath) {
      sessionStorage.setItem('ssoRedirectPath', redirectPath);
    }
    instance.loginRedirect();
  };

  const handleLogin = async (e: any) => {
    e.preventDefault();
    if (email && password) {
      const postData = new FormData();
      postData.append('user_name', email.toLowerCase());
      postData.append('password', password);
      postData.append('source', 'Native');

      // await post('user-manager/user_authentication', postData).then((response: any) => {
      await post('user-manager/cookie_user_authentication', postData).then((response: any) => {
        const { status } = response;
        if ([200, 201].includes(status) && response.data.messages[0] && !response.data.errors[0]) {
          const userData = response.data.messages[0];
          localStorage.setItem('source', 'Native');
          sessionStorage.removeItem('sessionExpired');
          const user = {
            email: userData.email,
            name: `${userData.first_name} ${userData.last_name}`,
            exp: userData.token_expiry,
            accountId: userData.user_id,
            token: userData.token,
            role: userData.role,
            user_id: userData.user_id,
            landing_screen: userData.landing_screen
          };

          login(user, location.search);
        } else if ([200, 201].includes(status) && response.data.errors[0]) {
          toastBottom.current.show({ severity: 'error', summary: 'Error', detail: response.data.errors[0] });
        }
      })
        .catch((error: any) => {
          // setLoading(false);
          if ([401, 404, 500].includes(error?.request?.status)) {
            handleError(error, toastBottom);
            navigate('/login');
          }
        });
    }
  };
  return (
    <div className="login-main">
      <Toast ref={toast} />
      <Toast ref={toastBottom} position="bottom-right" />

      <h1 className="page-title mt-2 mb-2 text-center">Get Started Now</h1>
      <div className="auth-desc">Enter your credentials to access your account</div>
      <form onSubmit={handleLogin}>
        <div className="field auth-form-item">
          <label className="fieldlabel" htmlFor="email">Email</label>
          <div className="auth-field-container">
            <IconField iconPosition="left">
              <InputIcon>
                <img src={MailIcon} alt="mail" />
              </InputIcon>
              <InputText
                className="w-full"
                id="email"
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </IconField>
          </div>
        </div>
        <div className="field auth-form-item">
          <label className="fieldlabel" htmlFor="password">Password</label>
          <div className="auth-field-container">
            <IconField iconPosition="left">
              <InputIcon>
                <img src={LockIcon} alt="mail" />
              </InputIcon>
              <InputText
                className="w-full"
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </IconField>
          </div>
        </div>
        <div className="flex flex-column md:flex-row justify-content-between">
          <div>
            <div className="flex align-items-center">
              <Checkbox inputId="isRemember" onChange={(e: any) => setRememberMe(e.checked)} checked={rememberMe} />
              <label htmlFor="isRemember" className="checkboxlabel ml-2">Remember Me</label>
            </div>
          </div>
          <div className="cursor-pointer auth-link" onClick={() => navigate('/reset-password')}>Forgot Password?</div>
        </div>
        <Button
          type="submit"
          className="w-full mt-4"
          label={apiLoading ? 'Loading...' : 'Login'}
          raised
          onClick={handleLogin}
          disabled={!email || !password}
          loading={apiLoading}
          loadingIcon="pi pi-spin pi-spinner"
          iconPos="right"
        />
      </form>
      {/*
      <div className="signup-msg">
        Don&apos;t have an account?&nbsp;
        <span className="text-highlight cursor-pointer" onClick={() => navigate('/signup')}>Sign up</span> 
      </div>
      */}
      <div className="flex mt-4 mb-4 justify-content-center or-divider">
        OR
      </div>
      <Button type="button" className="w-full cus-outline-btn justify-content-center" outlined onClick={() => initializeSignIn()}>
        <img src={SSOIcon} alt="sso" className="mr-3" />
        Continue with SSO
      </Button>
      <div className="help-msg-btm">
        Need help?&nbsp;
        <span className="text-highlight cursor-pointer" onClick={(e) => navigate('/help-support')}>Contact Support</span>
      </div>
    </div>
  );
};

export default Login;
